/*
 * Copyright (c) 2018-2019, F5 Networks, Inc. All rights reserved.
 *
 * No part of this software may be reproduced or transmitted in any
 * form or by any means, electronic or mechanical, for any purpose,
 * without express written permission of F5 Networks, Inc.
 */
import * as React from 'react';
import WebtopSectionModel, { WEBTOP_SECTION_SORT, WEBTOP_SECTION_STATE } from './Model';
import { IResourceId, IResourceStore, IWebtopResourceModel } from 'apmui/resource';
import WebtopResourceView from 'apmui/resource/WebtopResourceView';
import WebtopFolderModel from 'apmui/resource/webtopFolder/Model';
import WebtopFolderView from 'apmui/resource/webtopFolder/View';
import RESOURCE from 'apmui/resource/ResourceType';
import bind from 'apmui/decorators/bind';
import componentStyle from 'apmui/util/componentStyle';
import onKeyPress from 'apmui/util/onKeyPress';

interface IWebtopSectionProps {
    section: WebtopSectionModel;
    resources: IResourceStore;
    searchPattern: string;
}

interface IWebtopSectionState {
    expanded: boolean;
    folder?: WebtopFolderModel;
}

declare module 'react' {
    /* eslint-disable @typescript-eslint/interface-name-prefix */
    interface HTMLAttributes<T> {
        open?: boolean;
    }
}

const E = componentStyle.bind(null, 'apmui-expandable');
const WS = componentStyle.bind(null, 'apmui-webtop-section');

class WebtopSectionView extends React.Component<IWebtopSectionProps, IWebtopSectionState> {
    resourceRef: HTMLElement | null;
    captionRef: HTMLElement | null;

    state: IWebtopSectionState = {
        expanded: this.props.section.initExpanded === WEBTOP_SECTION_STATE.EXPANDED,
    };

    @bind
    setResourceRef(element: HTMLElement | null) {
        this.resourceRef = element;
    }

    @bind
    setCaptionRef(element: HTMLElement | null) {
        this.captionRef = element;
    }

    @bind
    latchExpanded() {
        this.setState((state: IWebtopSectionState) => ({
            expanded: !state.expanded,
        }));
    }

    @bind
    onResourceLaunch(resource: IWebtopResourceModel) {
        if (resource.type === RESOURCE.WEBTOP_FOLDER) {
            this.setState({ folder: resource });
            return true;
        }
        return false;
    }

    @bind
    onCloseFolder() {
        this.setState({ folder: undefined });
    }

    renderResources() {
        const resources = this.props.resources;
        const sort = this.props.section.sort;
        const searchPatternLow = this.props.searchPattern.toLocaleLowerCase();
        return this.props.section.assignedResources
            .filter((e) => resources[e.type] && resources[e.type][e.id])
            .map((e: IResourceId): IWebtopResourceModel => resources[e.type][e.id])
            .filter(
                /* hide resources assigned to folders if not searching */
                (res) => {
                    if (searchPatternLow !== '') {
                        return true;
                    } else {
                        if (!('folder' in res) || !res.folder) {
                            return true;
                        }
                    }
                    return false;
                }
            )
            .filter(
                /* hide currently opened folder */
                (res) => res !== this.state.folder
            )
            .filter(
                /*Hide unpublished on webtop*/
                (res) => !('hidden' in res) || !res.hidden
            )
            .filter((res: IWebtopResourceModel) => res.hasSearchPatternLow(searchPatternLow))
            .sort((a, b) => {
                if (a.type !== b.type) {
                    if (a.type === RESOURCE.WEBTOP_FOLDER) {
                        return -1;
                    }
                    if (b.type === RESOURCE.WEBTOP_FOLDER) {
                        return 1;
                    }
                }
                switch (sort) {
                    case WEBTOP_SECTION_SORT.ALPHABETICALLY:
                        return a.id.toLowerCase().localeCompare(b.id.toLowerCase());
                        break;
                    case WEBTOP_SECTION_SORT.BY_CAPTION:
                        return a.caption.toLowerCase().localeCompare(b.caption.toLowerCase());
                        break;
                }
                return 0;
            })
            .map((resource) => (
                <WebtopResourceView
                    key={resource.type + ':' + resource.id}
                    onLaunch={this.onResourceLaunch}
                    resource={resource}
                    section={this.props.section.id}
                />
            ));
    }

    render() {
        const resources = this.renderResources();

        const folderBrowser = this.state.folder && (
            <WebtopFolderView
                key={this.state.folder.id}
                parentId={this.props.section.id}
                folder={this.state.folder}
                resources={this.props.resources}
                onClose={this.onCloseFolder}
            />
        );

        const opened = this.state.expanded || Boolean(this.props.searchPattern);
        return (
            <div className={E('&', WS('&'))} open={opened} id={this.props.section.id}>
                <div
                    className={E('&--caption', WS('&--caption'))}
                    ref={this.setCaptionRef}
                    onClick={this.latchExpanded}
                    onKeyPress={onKeyPress(this.latchExpanded)}
                    tabIndex={0}
                    role="button"
                    aria-expanded={opened}
                    aria-controls={this.props.section.id}>
                    {this.props.section.caption}
                </div>
                {folderBrowser}
                <div className={WS('&--body')} ref={this.setResourceRef}>
                    {resources}
                </div>
            </div>
        );
    }

    componentDidUpdate(_: any, prevState: IWebtopSectionState) {
        if (!this.state.folder && prevState.folder && this.resourceRef) {
            // folder closed: set focus to folder resource
            let resource = this.resourceRef.querySelector(`#${prevState.folder.type}\\:${prevState.folder.id}`);
            if (!resource) {
                // folder resource is invisible: select 1st visible resource
                resource = this.resourceRef.querySelector('.apmui-webtop-resource');
            }
            if (resource) {
                (resource as HTMLElement).focus({ preventScroll: false });
            } else {
                // there is no visible resources due to search pattern: select section caption
                if (this.captionRef) {
                    this.captionRef.focus({ preventScroll: false });
                }
            }
        }
    }
}

export { WebtopSectionView, WebtopSectionView as default };
